'use client';

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import NextImage from 'next/image';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import siteTheme from '@/lib/theme';

const { fonts } = siteTheme;

export interface LightboxImage {
  url: string;
  alt?: string;
}

interface Props {
  images: LightboxImage[];
  startIndex?: number;
  onClose: () => void;
}

export default function LightboxGallery({ images, startIndex = 0, onClose }: Props) {
  const [current, setCurrent] = useState(startIndex);

  const go   = useCallback((n: number) => setCurrent((n + images.length) % images.length), [images.length]);
  const prev = useCallback(() => go(current - 1), [go, current]);
  const next = useCallback(() => go(current + 1), [go, current]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [prev, next, onClose]);

  if (typeof window === 'undefined') return null;

  const img = images[current];

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(10,10,10,0.96)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes lbIn { from { opacity:0 } to { opacity:1 } }
        .lb-wrap { animation: lbIn 0.2s ease; }
        .lb-thumb { position:relative; width:7.2rem; height:5rem; flex-shrink:0;
          background:none; border:2px solid transparent; padding:0; cursor:pointer;
          opacity:0.55; transition: opacity 0.2s, border-color 0.2s; }
        .lb-thumb:hover { opacity:0.85; }
        .lb-thumb.active { border-color:#4fd1d9; opacity:1; }
        .lb-arrow { position:absolute; top:50%; transform:translateY(-50%);
          background:rgba(255,255,255,0.08); border:none; cursor:pointer;
          color:#fff; width:4.8rem; height:4.8rem;
          display:flex; align-items:center; justify-content:center;
          opacity:0.7; transition:opacity 0.2s; }
        .lb-arrow:hover { opacity:1; }
      ` }} />

      <div className="lb-wrap" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* ── Top bar ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.6rem 2.4rem', flexShrink: 0,
        }}>
          <span style={{ fontFamily: fonts.sans, fontSize: '1.6rem', fontWeight: 300, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.1em' }}>
            {current + 1} / {images.length}
          </span>
          <span style={{ fontFamily: fonts.script, fontSize: '2.4rem', fontWeight: 400, color: 'rgba(255,255,255,0.4)' }}>
            Sandy Resort
          </span>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', padding: '0.4rem',
          }}>
            <X size={24} strokeWidth={1.5} />
          </button>
        </div>

        {/* ── Main image ── */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 0, padding: '0 7rem' }}>
          {images.length > 1 && (
            <button onClick={prev} className="lb-arrow" style={{ left: '1.2rem' }} aria-label="Previous">
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
          )}

          <div style={{ position: 'relative', width: '100%', height: '100%', maxWidth: '100rem' }}>
            <NextImage
              key={current}
              src={img.url}
              alt={img.alt || 'Room gallery'}
              fill
              style={{ objectFit: 'contain' }}
              sizes="(max-width: 768px) 100vw, 100rem"
              priority
            />
          </div>

          {images.length > 1 && (
            <button onClick={next} className="lb-arrow" style={{ right: '1.2rem' }} aria-label="Next">
              <ChevronRight size={20} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* ── Thumbnail strip ── */}
        {images.length > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'center',
            gap: '0.8rem', padding: '1.6rem 2.4rem', flexShrink: 0, overflowX: 'auto',
          }}>
            {images.map((th, i) => (
              <button key={i} onClick={() => go(i)} className={`lb-thumb${i === current ? ' active' : ''}`} aria-label={`Image ${i + 1}`}>
                <NextImage src={th.url} alt={th.alt || `Thumb ${i + 1}`} fill style={{ objectFit: 'cover' }} sizes="72px" />
              </button>
            ))}
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}
